import { useEffect, useState, useMemo } from "react";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Users, PieChart, CheckCircle2, MessageSquareWarning, Link as LinkIcon, Ban, RotateCcw } from "lucide-react";

const COMM_METHODS = ["Email", "Phone", "Text", "Slack", "Microsoft Teams", "Other"];
const TOOLS = ["Google Workspace", "Google Calendar", "Microsoft 365", "Outlook", "Zoom", "Microsoft Teams", "Slack", "Canva", "QuickBooks", "CRM", "Project Management Software", "Cloud Storage", "Other"];

const emptyForm = () => ({
  business_info: { name: "", website: "", industry: "", description: "", time_zone: "", business_hours: "" },
  services_priorities: { services_being_provided: "", top_priorities: "", recurring_tasks: "", current_challenges: "", short_term_goals: "", long_term_goals: "", special_instructions: "" },
  communication_prefs: { methods: [], best_days: "", best_times: "", response_method: "", meeting_required: false, meeting_frequency: "", meeting_day: "", meeting_time: "" },
  tools_platforms: { tools: [], other_tools: "", additional_instructions: "" },
  work_preferences: { prioritization: "", communication_style: "", approval_process: "", approver: "", deadlines: "", additional: "" },
  final_confirmation: { additional_info: "", confirmed: false, signature: "", signed_at: "" },
});

function computeCompletion(form) {
  const sections = [
    ["business_info", ["name", "website", "industry", "description", "time_zone", "business_hours"]],
    ["services_priorities", ["services_being_provided", "top_priorities", "recurring_tasks", "current_challenges", "short_term_goals", "long_term_goals", "special_instructions"]],
    ["communication_prefs", ["best_days", "best_times", "response_method"]],
    ["tools_platforms", ["other_tools", "additional_instructions"]],
    ["work_preferences", ["prioritization", "communication_style", "approval_process", "approver", "deadlines", "additional"]],
    ["final_confirmation", ["signature", "confirmed"]],
  ];
  let total = 0, filled = 0;
  for (const [sec, keys] of sections) {
    for (const k of keys) {
      total += 1;
      const v = form?.[sec]?.[k];
      if (v === true || (typeof v === "string" && v.trim().length > 0)) filled += 1;
    }
  }
  total += 2;
  if ((form?.communication_prefs?.methods || []).length > 0) filled += 1;
  if ((form?.tools_platforms?.tools || []).length > 0) filled += 1;
  return Math.round((filled / total) * 100);
}

/** Inline Onboarding workspace. Renders the ACTUAL fillable intake form. */
export default function OnboardingPanel({ client, recordId, onSaved, onContext }) {
  const { currentOrgId } = useAuth();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState("in_progress");
  const [loading, setLoading] = useState(false);
  const completion = useMemo(() => computeCompletion(form), [form]);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    (async () => {
      try {
        let existing = null;
        if (recordId) {
          const { data } = await supabase.from("client_onboarding").select("*").eq("id", recordId).limit(1).maybeSingle();
          existing = data;
        }
        if (!existing) existing = await apiFetch(`/onboarding/by-client/${client.id}`).catch(() => null);
        const base = emptyForm();
        if (existing) {
          setEditing(existing);
          setForm({
            business_info: { ...base.business_info, ...(existing.business_info || {}) },
            services_priorities: { ...base.services_priorities, ...(existing.services_priorities || {}) },
            communication_prefs: { ...base.communication_prefs, ...(existing.communication_prefs || {}) },
            tools_platforms: { ...base.tools_platforms, ...(existing.tools_platforms || {}) },
            work_preferences: { ...base.work_preferences, ...(existing.work_preferences || {}) },
            final_confirmation: { ...base.final_confirmation, ...(existing.final_confirmation || {}) },
          });
          setStatus(existing.status || "in_progress");
        } else {
          setEditing(null);
          setForm({ ...base, business_info: { ...base.business_info, name: client.referral_source || "" } });
          setStatus("in_progress");
        }
      } finally { setLoading(false); }
    })();
    /* eslint-disable-next-line */
  }, [client?.id, recordId]);

  useEffect(() => {
    onContext && onContext({
      client, status, completion, editing,
      actions: [
        // Only expose Save & Submit while the intake is still active.
        ...(!editing || editing.status !== "cancelled" ? [
          { key: "save", label: "Save progress", variant: "outline", testid: "onb-save-progress", onClick: () => save("in_progress") },
          { key: "submit", label: "Submit onboarding", variant: "primary", testid: "onb-submit", onClick: () => save("submitted") },
        ] : []),
        ...(editing && editing.status !== "cancelled" ? [
          { key: "send", label: "Send / copy link", icon: LinkIcon, variant: "outline", testid: "onb-send-link", onClick: sendLink },
          { key: "changes", label: "Request changes", icon: MessageSquareWarning, variant: "outline", testid: "onb-request-changes", onClick: requestChanges },
        ] : []),
        ...(editing && ["submitted", "resubmitted", "under_review"].includes(editing.status) ? [
          { key: "approve", label: "Approve", icon: CheckCircle2, variant: "primary", testid: "onb-approve", onClick: approve },
        ] : []),
        // Cancel / Reopen — Onboarding has no backend DELETE endpoint, so this
        // is our safe destructive action (soft-cancel via status="cancelled").
        ...(editing && editing.status !== "cancelled" ? [
          { key: "cancel", label: "Cancel intake", icon: Ban, variant: "outline", testid: "onb-cancel",
            onClick: () => cancelIntake() },
        ] : []),
        ...(editing && editing.status === "cancelled" ? [
          { key: "reopen", label: "Reopen intake", icon: RotateCcw, variant: "outline", testid: "onb-reopen",
            onClick: () => save("in_progress") },
        ] : []),
      ],
    });
    /* eslint-disable-next-line */
  }, [form, status, completion, editing]);

  const cancelIntake = async () => {
    if (!editing) return;
    if (!window.confirm(`Cancel this onboarding intake? The record is preserved as an audit trail; you can Reopen it later.`)) return;
    await save("cancelled");
  };

  const save = async (newStatus) => {
    if (!client) return toast.error("Pick a client first");
    if (newStatus === "submitted" && !form.final_confirmation.confirmed) return toast.error("Client confirmation required to submit");
    if (newStatus === "submitted" && !form.final_confirmation.signature?.trim()) return toast.error("Signature required to submit");
    const payload = {
      organization_id: currentOrgId, client_id: client.id,
      business_info: form.business_info,
      services_priorities: form.services_priorities,
      communication_prefs: form.communication_prefs,
      tools_platforms: form.tools_platforms,
      work_preferences: form.work_preferences,
      document_ids: [],
      final_confirmation: { ...form.final_confirmation, signed_at: form.final_confirmation.signed_at || (newStatus === "submitted" ? new Date().toISOString() : "") },
      status: newStatus || status,
    };
    try {
      const r = await apiFetch(`/onboarding`, { method: "POST", body: JSON.stringify(payload) });
      toast.success(newStatus === "submitted" ? "Onboarding submitted" : "Onboarding saved");
      onSaved && onSaved({ kind: "onboarding", id: r?.id || editing?.id });
    } catch (e) { toast.error(e.message); }
  };
  const sendLink = async () => {
    try {
      const r = await apiFetch("/onboarding/send-link", { method: "POST", body: JSON.stringify({ organization_id: currentOrgId, client_id: client.id }) });
      const link = r.link || `${window.location.origin}/portal/onboarding/${r.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success(r.email_sent ? "Link emailed (and copied)" : "Link copied");
    } catch (e) { toast.error(e.message); }
  };
  const requestChanges = async () => {
    if (!editing) return;
    const section = window.prompt("Which section (A-G)?", "A");
    if (!section) return;
    const s = section.trim().toUpperCase();
    if (!"ABCDEFG".includes(s)) return toast.error("Enter A-G");
    const note = window.prompt(`What should the client update in Section ${s}?`);
    if (!note?.trim()) return;
    try {
      await apiFetch(`/onboarding/${editing.id}/request-changes`, { method: "POST", body: JSON.stringify({ section: s, note: note.trim() }) });
      toast.success("Change request sent");
      onSaved && onSaved({ kind: "onboarding", id: editing.id });
    } catch (e) { toast.error(e.message); }
  };
  const approve = async () => {
    if (!editing) return;
    try {
      await apiFetch(`/onboarding/${editing.id}/approve`, { method: "POST" });
      toast.success("Onboarding approved");
      onSaved && onSaved({ kind: "onboarding", id: editing.id });
    } catch (e) { toast.error(e.message); }
  };

  const toggleArr = (section, key, v) => setForm((f) => {
    const cur = f[section][key] || [];
    return { ...f, [section]: { ...f[section], [key]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] } };
  });

  if (loading) return <div className="text-center py-16 text-sm text-[#57534E]">Loading onboarding…</div>;

  return (
    <div className="space-y-4" data-testid="workspace-onboarding">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#57534E]">Basic Client Intake</div>
          <div className="text-lg font-semibold font-display">{client ? `${client.first_name} ${client.last_name}` : "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#57534E]">Completion</div>
          <div className="text-lg font-semibold text-[#2C3A29]" data-testid="onb-completion">{completion}%</div>
        </div>
      </div>
      <div className="w-full h-2 rounded-full bg-[#F5F1EA] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#E8712C] to-[#C55A1F] transition-all duration-500" style={{ width: `${completion}%` }} />
      </div>

      <Section title="A. Business Information" icon={ShieldCheck}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Business/Organization name"><Input data-testid="onb-biz-name" value={form.business_info.name} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, name: e.target.value } }))} /></Field>
          <Field label="Website"><Input data-testid="onb-biz-web" value={form.business_info.website} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, website: e.target.value } }))} /></Field>
          <Field label="Industry"><Input value={form.business_info.industry} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, industry: e.target.value } }))} /></Field>
          <Field label="Time zone *"><Input placeholder="e.g. America/New_York" data-testid="onb-tz" value={form.business_info.time_zone} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, time_zone: e.target.value } }))} /></Field>
          <Field label="Business description" wide><Textarea rows={2} value={form.business_info.description} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, description: e.target.value } }))} /></Field>
          <Field label="Business hours" wide><Input placeholder="e.g. Mon-Fri 9am-5pm" value={form.business_info.business_hours} onChange={(e) => setForm((f) => ({ ...f, business_info: { ...f.business_info, business_hours: e.target.value } }))} /></Field>
        </div>
      </Section>

      <Section title="B. Services & Priorities" icon={PieChart}>
        {[
          ["services_being_provided", "Services being provided *"],
          ["top_priorities", "Top priorities"],
          ["recurring_tasks", "Important recurring tasks"],
          ["current_challenges", "Current challenges"],
          ["short_term_goals", "Short-term goals"],
          ["long_term_goals", "Long-term goals"],
          ["special_instructions", "Special instructions"],
        ].map(([k, l]) => (
          <Field key={k} label={l}>
            <Textarea rows={2} data-testid={`onb-sp-${k}`} value={form.services_priorities[k]}
              onChange={(e) => setForm((f) => ({ ...f, services_priorities: { ...f.services_priorities, [k]: e.target.value } }))} />
          </Field>
        ))}
      </Section>

      <Section title="C. Communication Preferences" icon={Users}>
        <Field label="Preferred communication method">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
            {COMM_METHODS.map((m) => (
              <label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${(form.communication_prefs.methods || []).includes(m) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
                <input type="checkbox" checked={(form.communication_prefs.methods || []).includes(m)}
                  onChange={() => toggleArr("communication_prefs", "methods", m)} className="accent-[#E8712C]" />{m}
              </label>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Best days to contact"><Input value={form.communication_prefs.best_days} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, best_days: e.target.value } }))} /></Field>
          <Field label="Best times to contact"><Input value={form.communication_prefs.best_times} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, best_times: e.target.value } }))} /></Field>
          <Field label="Preferred response method"><Input value={form.communication_prefs.response_method} onChange={(e) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, response_method: e.target.value } }))} /></Field>
          <Field label="Regular meeting required?">
            <Select value={form.communication_prefs.meeting_required ? "yes" : "no"} onValueChange={(v) => setForm((f) => ({ ...f, communication_prefs: { ...f.communication_prefs, meeting_required: v === "yes" } }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      <Section title="D. Tools & Platforms" icon={PieChart}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
          {TOOLS.map((t) => (
            <label key={t} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${(form.tools_platforms.tools || []).includes(t) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
              <input type="checkbox" checked={(form.tools_platforms.tools || []).includes(t)} onChange={() => toggleArr("tools_platforms", "tools", t)} className="accent-[#E8712C]" />{t}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field label="Other tools"><Input value={form.tools_platforms.other_tools} onChange={(e) => setForm((f) => ({ ...f, tools_platforms: { ...f.tools_platforms, other_tools: e.target.value } }))} /></Field>
          <Field label="Additional instructions"><Input value={form.tools_platforms.additional_instructions} onChange={(e) => setForm((f) => ({ ...f, tools_platforms: { ...f.tools_platforms, additional_instructions: e.target.value } }))} /></Field>
        </div>
        <p className="text-[11px] text-[#B02222] mt-3">Never enter passwords, API keys, or security codes here.</p>
      </Section>

      <Section title="E. Work Preferences" icon={Users}>
        {[
          ["prioritization", "How should tasks be prioritized?"],
          ["communication_style", "Preferred communication style"],
          ["approval_process", "Approval process"],
          ["approver", "Who approves completed work?"],
          ["deadlines", "Important deadlines"],
          ["additional", "Additional work preferences"],
        ].map(([k, l]) => (
          <Field key={k} label={l}><Textarea rows={2} value={form.work_preferences[k]} onChange={(e) => setForm((f) => ({ ...f, work_preferences: { ...f.work_preferences, [k]: e.target.value } }))} /></Field>
        ))}
      </Section>

      <Section title="F. Final Confirmation" icon={ShieldCheck}>
        <Field label="Additional information"><Textarea rows={2} value={form.final_confirmation.additional_info} onChange={(e) => setForm((f) => ({ ...f, final_confirmation: { ...f.final_confirmation, additional_info: e.target.value } }))} /></Field>
        <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E8E2D9] bg-white cursor-pointer">
          <input type="checkbox" checked={!!form.final_confirmation.confirmed} onChange={(e) => setForm((f) => ({ ...f, final_confirmation: { ...f.final_confirmation, confirmed: e.target.checked } }))} className="w-4 h-4 mt-0.5 accent-[#E8712C]" data-testid="onb-confirm" />
          <span className="text-sm">I confirm the information provided in this onboarding form is accurate and complete.</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Electronic signature (full name)"><Input data-testid="onb-signature" value={form.final_confirmation.signature} onChange={(e) => setForm((f) => ({ ...f, final_confirmation: { ...f.final_confirmation, signature: e.target.value } }))} /></Field>
          <Field label="Date signed"><Input readOnly value={form.final_confirmation.signed_at ? new Date(form.final_confirmation.signed_at).toLocaleDateString() : new Date().toLocaleDateString()} className="bg-[#FAF8F5]" /></Field>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#57534E]">
        {Icon && <Icon size={14} strokeWidth={1.75} />}{title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, wide, children }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
